/**
 * The words a tool-call row leads with, in the three tenses a row can be in.
 *
 * A row that says "Pushed to feat/x" over a rejected push states an outcome
 * that never happened, so every verb needs a running form and a failed form
 * beside its completed one. Writing all three out by hand is how the tables
 * grew: "Pushed" / "Pushing" / "Couldn't push" repeats the same stem three
 * times, sixty times over, and a typo in any one of them is invisible.
 *
 * So a verb is named once, in the plain form it reads as in an instruction
 * ("push", "search for", "run tests"), and the tenses are built from it:
 *
 * - `done` — past tense, for a call that finished: "Pushed to feat/x".
 * - `active` — present participle, while it runs: "Pushing to feat/x".
 * - `failed` — what to say when it didn't work: "Couldn't push".
 *
 * Only English's irregularities are written down, and only once each:
 * {@link IRREGULAR_PAST} for the verbs `-ed` gets wrong, {@link DOUBLED} for
 * the ones whose final consonant doubles. Everything else follows the rules,
 * and a verb whose copy reads better as a noun ("Build failed") says so with a
 * `failed` override rather than by spelling out all three forms again.
 */

/** Which tense a row speaks in, chosen from the call's status. */
export type Tense = 'done' | 'active' | 'failed';

/** One verb's words, one per tense. */
export type VerbWords = Record<Tense, string>;

export interface VerbOptions {
  /**
   * Which word of the phrase inflects, when it isn't the first: "type check"
   * becomes "Type checked", not "Typed check".
   */
  inflect?: number;
  /**
   * Copy for a failed call, where "Couldn't <phrase>" isn't the best words for
   * it: a failed `pnpm build` reads better as "Build failed".
   */
  failed?: string;
}

/** Past tenses English doesn't build by suffixing `-ed`. */
const IRREGULAR_PAST: Record<string, string> = {
  build: 'built',
  find: 'found',
  read: 'read',
  reset: 'reset',
  run: 'ran',
  write: 'wrote',
};

/**
 * Verbs whose final consonant doubles before a suffix: "commit" → "committed".
 * Listed rather than derived — the spelling rule turns on which syllable is
 * stressed, so the same letters double in "commit" and don't in "open".
 */
const DOUBLED = new Set(['commit', 'drop', 'format', 'pop', 'reset', 'run', 'tag']);

function pastTense(word: string): string {
  const irregular = IRREGULAR_PAST[word];
  if (irregular) return irregular;
  if (DOUBLED.has(word)) return `${word}${word.slice(-1)}ed`;
  if (word.endsWith('e')) return `${word}d`;
  if (/[^aeiou]y$/.test(word)) return `${word.slice(0, -1)}ied`;
  return `${word}ed`;
}

function participle(word: string): string {
  if (DOUBLED.has(word)) return `${word}${word.slice(-1)}ing`;
  // A silent `e` is dropped before `-ing` ("stage" → "staging") but kept in the
  // past ("staged"), so the two forms don't share a stem.
  return `${word.endsWith('e') ? word.slice(0, -1) : word}ing`;
}

function inflect(words: readonly string[], at: number, form: (word: string) => string): string {
  const phrase = words.map((word, i) => (i === at ? form(word) : word)).join(' ');
  return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}

/** The three tenses of one verb phrase, written in its plain form: `search for`. */
export function verb(phrase: string, options: VerbOptions = {}): VerbWords {
  const words = phrase.split(' ');
  const at = options.inflect ?? 0;
  return {
    done: inflect(words, at, pastTense),
    active: inflect(words, at, participle),
    failed: options.failed ?? `Couldn't ${phrase}`,
  };
}

/**
 * A table of verbs keyed by their plain form, so a caller names each phrase
 * once and gets a `keyof` union of exactly the verbs it declared — a verb the
 * table doesn't know is a type error, not a row with no words for a tense.
 */
export function verbs<T extends Record<string, VerbOptions>>(specs: T): Record<keyof T, VerbWords> {
  return Object.fromEntries(
    Object.entries(specs).map(([phrase, options]) => [phrase, verb(phrase, options)]),
  ) as Record<keyof T, VerbWords>;
}
